import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { QUALITY_LADDER, RELAY_CAP_LABEL, rungLabel } from "../../../lib/qualityLadder";
import {
  cachedPlayerPrefs,
  fetchPlayerPrefs,
  playerPrefsPatch,
  savePlayerPrefs,
  type PlayerPrefs,
  type StreamQualityCap,
} from "../../../lib/playerPrefs";
import { Checkbox, Field, Select } from "../../../components/ui";
import {
  SheetHeader,
  SheetSection,
  UnsavedBar,
  useLastTouched,
} from "../../../components/settings-sheet";

export const Route = createFileRoute("/_authed/settings/quality")({
  component: QualitySettings,
});

/** "max" | "<res>:<kbps>" — the Select's value encoding for a cap. */
function capValue(c: StreamQualityCap): string {
  return c.maxResolution === null || c.maxBitrateKbps === null
    ? "max"
    : `${c.maxResolution}:${c.maxBitrateKbps}`;
}

function parseCap(v: string): StreamQualityCap {
  if (v === "max") return { maxResolution: null, maxBitrateKbps: null };
  const [res, kbps] = v.split(":");
  return { maxResolution: Number(res), maxBitrateKbps: Number(kbps) };
}

function CapSelect(props: { id: string; value: StreamQualityCap; onChange: (c: StreamQualityCap) => void }) {
  return (
    <Select id={props.id} value={capValue(props.value)} onChange={(v) => props.onChange(parseCap(v))}>
      <option value="max">Maximum</option>
      {QUALITY_LADDER.map((r) => (
        <option key={rungLabel(r)} value={`${r.maxResolution}:${r.maxBitrateKbps}`}>
          {rungLabel(r)}
        </option>
      ))}
    </Select>
  );
}

/**
 * Account-level playback quality defaults (the Plex Web — Quality page).
 * Synced through the cloud account so the same defaults follow the user
 * onto other devices and the future mobile apps. Edits sit in a draft
 * until the floating "Unsaved changes" pill's Save (settings-sheet
 * UnsavedBar, as on Settings › Trakt; Kenny); the in-player quality menu
 * overrides these per playback.
 */
function QualitySettings() {
  const qc = useQueryClient();
  const settings = useQuery({
    queryKey: ["me", "settings"],
    queryFn: fetchPlayerPrefs,
    // Offline / cloud unreachable: the localStorage mirror still applies.
    placeholderData: cachedPlayerPrefs(),
    retry: 1,
  });

  // baseline → draft → patch (the account page idiom): a null draft shows
  // the baseline; re-seed only while clean, so a background refetch never
  // clobbers edits in progress.
  const baseline = useMemo(() => settings.data ?? cachedPlayerPrefs(), [settings.data]);
  const [draft, setDraft] = useState<PlayerPrefs | null>(null);
  const prevBaseline = useRef<PlayerPrefs | null>(null);
  useEffect(() => {
    const prev = prevBaseline.current;
    setDraft((d) => (d && prev && Object.keys(playerPrefsPatch(prev, d)).length === 0 ? null : d));
    prevBaseline.current = baseline;
  }, [baseline]);

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [offline, setOffline] = useState(false);

  const form = draft ?? baseline;
  const patch = playerPrefsPatch(baseline, form);
  const dirty = Object.keys(patch).length > 0;
  // The control changed last gets the focus back when the pill leaves; the
  // home quality select goes when a revert ticks its checkbox back on.
  const touched = useLastTouched(() => document.getElementById("home-recommended"));

  function update(p: Partial<PlayerPrefs>) {
    setDraft({ ...form, ...p });
    setSaved(false);
  }

  function revert() {
    setDraft(null);
  }

  async function save() {
    if (saving || !dirty) return;
    setSaving(true);
    setSaved(false);
    try {
      const merged = await savePlayerPrefs(patch);
      qc.setQueryData(["me", "settings"], merged);
      setOffline(false);
    } catch {
      // The mirror already holds the change (written before the PATCH) and
      // the player plays by it; the account copy catches up next time the
      // cloud is reachable. Keep the page in step with the mirror.
      qc.setQueryData(["me", "settings"], { ...baseline, ...patch });
      setOffline(true);
    } finally {
      setDraft(null);
      setSaved(true);
      setSaving(false);
    }
  }

  return (
    // .rago-sheet-fill: the root fills the sheet, so the pill docks at its foot.
    <div className="rago-sheet-fill">
      <SheetHeader
        title="Quality"
        lede="These defaults follow your account on every device. The player's quality menu overrides them for a single video."
      />

      {/* Every control's change bubbles to here: the one changed last. */}
      <div onChange={touched.onChange}>
        <SheetSection title="Internet streaming">
          <Field
            label="Video quality"
            htmlFor="remote-quality"
            hint={`Default quality when streaming away from home. Set it too high and playback starts slowly and pauses often. Over the Rago relay, video is limited to ${RELAY_CAP_LABEL}.`}
          >
            <CapSelect
              id="remote-quality"
              value={form.remoteQuality}
              onChange={(c) => update({ remoteQuality: c })}
            />
          </Field>
          <Checkbox
            id="play-smaller-original"
            checked={form.playSmallerOriginal}
            onChange={(v) => update({ playSmallerOriginal: v })}
            label="Play smaller videos at original quality"
            hint="When a video's own bitrate is below the quality above, play the original file instead of converting it."
          />
        </SheetSection>

        <SheetSection title="Home streaming">
          <Checkbox
            id="home-recommended"
            checked={form.homeUseRecommended}
            onChange={(v) => update({ homeUseRecommended: v })}
            label="Use recommended settings"
            hint="On your local network, compatible videos play at original quality and incompatible ones convert at maximum quality."
          />
          {!form.homeUseRecommended ? (
            <div className="mt-4">
              <Field label="Video quality" htmlFor="home-quality">
                <CapSelect
                  id="home-quality"
                  value={form.homeQuality}
                  onChange={(c) => update({ homeQuality: c })}
                />
              </Field>
            </div>
          ) : null}
        </SheetSection>

        <SheetSection title="Advanced">
          <Checkbox
            id="auto-adjust"
            checked={form.autoAdjust}
            disabled
            onChange={() => {}}
            label="Automatically adjust quality"
            hint="Step quality up and down based on connection speed. Coming soon."
          />
        </SheetSection>
      </div>

      {/* The root's direct child (its dock rides at the sheet's foot), and
          always rendered, as UnsavedBar asks. */}
      <UnsavedBar
        dirty={dirty}
        saving={saving}
        saved={saved}
        savedLabel={offline ? "Saved on this device" : undefined}
        onSave={() => void save()}
        onRevert={revert}
        onFocusLost={touched.focus}
      />
    </div>
  );
}
